import { useState } from 'react'

import type { TempUnits } from '@/insights/assets/historyGraph/types'
import type { AssetWindowState } from '@/insights/common/assetHistoryWindow'

import { AssetFuelGraph, AssetSpeedGraph } from './AssetSampleGraphs'
import AssetTemperatureGraph from './AssetTemperatureGraph'

type GraphKind = 'temperature' | 'fuel' | 'speed'

const GRAPHS: { kind: GraphKind; label: string }[] = [
  { kind: 'temperature', label: 'Temperature' },
  { kind: 'fuel', label: 'Fuel' },
  { kind: 'speed', label: 'Speed' },
]

interface AssetGraphsProps {
  assetId: number
  companyId: number | undefined
  units: TempUnits
  assetWindow: AssetWindowState
}

/**
 * The asset detail Graphs tab: Temperature (the default), Fuel or Speed, picked from a
 * dropdown at the left of the date bar like the History tab's. All three share the asset's
 * period, so the dates hold when switching graph. The pick isn't kept: it resets with the tab.
 */
export default function AssetGraphs({ assetId, companyId, units, assetWindow }: AssetGraphsProps) {
  const [graph, setGraph] = useState<GraphKind>('temperature')

  // Fuel and Speed need the company for their scope check; without one there's nothing to pick.
  const picker = companyId != null && (
    <select
      className="select select-sm h-8 w-fit text-xs"
      title="Graph"
      aria-label="Graph"
      value={graph}
      onChange={(e) => setGraph(e.target.value as GraphKind)}
    >
      {GRAPHS.map((g) => (
        <option key={g.kind} value={g.kind}>
          {g.label}
        </option>
      ))}
    </select>
  )

  if (graph === 'temperature' || companyId == null) {
    return (
      <AssetTemperatureGraph
        assetId={assetId}
        companyId={companyId}
        units={units}
        assetWindow={assetWindow}
        picker={picker}
      />
    )
  }
  const props = { assetId, companyId, assetWindow, picker }
  return graph === 'fuel' ? <AssetFuelGraph {...props} /> : <AssetSpeedGraph {...props} units={units} />
}
